
import {
  Container,
  Dropdown,
  Segment,
  Table,
  Header,
  Button,
  Image,
} from 'semantic-ui-react'
import { Link } from "react-router-dom";
import moment from 'moment';

import { useQuery, useMutation, gql } from '@apollo/client';

import { ResponsiveContainer } from '../ResponsiveContainer';
import { useState, useLayoutEffect } from 'react';
import { GET_TAGS as GET_POSSIBLE_TAGS } from '../../queries/tags';
import { PATHS } from '../../paths';
import { useIsAdmin } from '../../hooks/isAdmin';

import styles from './Dashboard.module.css';

const CANCELLATION_REASONS = [
    'Teacher sick',
    'Teacher holiday',
    'Company cancelled',
    'Other'
];

const DELETE_SESSION = gql`
mutation Mutation($sessionId: Int!, $reason: String) {
  cancelSession(sessionId: $sessionId, reason: $reason) {
    id
  }
}`;

// Deliberately fetches the whole day and filters by teacher on the client. The teacher picker's
// options are derived from these rows, so asking the server to filter made the list collapse to
// whoever was already selected — there was no way to switch to a second teacher, and a filter
// that matched nothing hid the picker along with the results.
const GET_SESSIONS = gql`
query SessionsForDateAndTag($date: String!, $filters: String) {
  sessionsForDateAndTag(date: $date, filters: $filters) {
    absentCount
    attendanceMarkingComplete
    attendancePercentage
    attendancePercentageNumerator
    attendancePercentageDenominator
    teacherMarkedAttendance
    startTime
    reasonForCancellation
    id
    course {
      name
      teachers {
        name
        id
        photoUrl
      }
      id
    }
  }
}`;

const Dashboard = () => {
    const isAdmin = useIsAdmin();
    // Read from local storage to get the date
  const [tagIds, setTagIds] = useState<string[]>(() => {
    const storedTags = localStorage.getItem('attendanceDashboardTags');
    return storedTags ? JSON.parse(storedTags) : [];
  })
  const [reasonForCancellation, setReasonForCancellation] = useState('Company cancelled');
  const [date, setDate] = useState(new Date());
  // '' is "All Teachers". Kept as the User.id number the options carry, so the Dropdown can match
  // the selection back to an option — stringifying it meant nothing ever looked selected.
  const [teacherId, setTeacherId] = useState<number | ''>('')
  const [deleteSession] = useMutation(DELETE_SESSION);
  const { loading, error, data, refetch } = useQuery(GET_SESSIONS, {
    variables: {
        filters: tagIds && tagIds.length > 0 ? tagIds.join(',') : "",
        date: date.toISOString().split('T')[0]
    }
  });

    const { loading: tagsLoading, error: tagsError, data: tagsData} = useQuery(GET_POSSIBLE_TAGS, {
        variables: {
            showPeopleTags: false,
            showCourseTags: true,
        }
    });

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useLayoutEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);


  if (error) {
      return <>Error</>
  }

     const teacherItem = (teacher:any) => {
        return (
            <>
                <Image avatar src={teacher.photoUrl} size='tiny' style={{ width: '2em', height: '2em', objectFit: 'cover' }}/>
                <Link to={`/people/${teacher.id}`}>
                    {teacher.name}
                </Link>
                <br />
            </>
        )
     }

     const renderRow = (session: any) => {
        const sessionStart = moment(Number(session.startTime));
        const isPast = sessionStart.isBefore(moment());
        return (
            <Table.Row className={isPast ? styles.pastSession : styles.upcomingSession} key={session.id}>
                <Table.Cell>{sessionStart.format('h:mm A')}</Table.Cell>
                <Table.Cell>
                    <Link to={`${PATHS.CLASSES}/${session.course.id}`}>
                        {session.course.name}
                    </Link>
                </Table.Cell>
                <Table.Cell>{session.reasonForCancellation ? 'Cancelled' : isPast && session.teacherMarkedAttendance ? Math.round(session.attendancePercentage*100) + '%': '❌'}</Table.Cell>
                <Table.Cell>{session.reasonForCancellation ? '-' : isPast && session.teacherMarkedAttendance ? session.absentCount : '-'}</Table.Cell>
                <Table.Cell>{session.reasonForCancellation ? '-' : isPast && session.attendanceMarkingComplete ? '✅': '❌'}</Table.Cell>
                <Table.Cell>{session.course.teachers.map(teacherItem) || '-'}</Table.Cell>
                <Table.Cell>{session.reasonForCancellation || '-'}</Table.Cell>
            </Table.Row>
        );
    };

    // Every session for the day and tag filter, whoever teaches it — the teacher picker's options
    // come from here, so it must stay unfiltered by teacher.
    const allSessions: any[] = (data ? data.sessionsForDateAndTag : []).filter((s: any) => s.course != null);

    const teacherOptions = [
        { key: 'all', text: 'All Teachers', value: '' as number | '' },
        ...Array.from(
            allSessions
                .flatMap((s: any) => s.course.teachers || [])
                .reduce((acc: Map<number, any>, t: any) => acc.has(t.id) ? acc : acc.set(t.id, t), new Map())
                .values()
        )
            .sort((a: any, b: any) => String(a.name).localeCompare(String(b.name)))
            .map((t: any) => ({ key: t.id, text: t.name, value: t.id as number | '' })),
    ];

    // The filter is "is this person on the class's teacher roster", matching exactly the list the
    // picker was built from and what the Teachers column shows.
    const sessions = teacherId === ''
        ? allSessions
        : allSessions.filter((s: any) => (s.course.teachers || []).some((t: any) => t.id === teacherId));

    let overallAttendance = 0;
    const denum = sessions.reduce((acc: number, s: any) => acc + (s.attendancePercentageDenominator || 0), 0);

    if (denum > 0) {
        const num = sessions.reduce((acc: number, s: any) => acc + (s.attendancePercentageNumerator || 0), 0);
        overallAttendance = Math.round((num / denum) * 100);
    }

    const overallAbsent = sessions.reduce((acc: number, s: any) => acc + (s.absentCount || 0), 0);


  return (
    <ResponsiveContainer>
      <Segment style={{ padding: '2em 0em' }} vertical>
        <Container>
            <Header as="h2" textAlign="center">
                {date.toISOString().split('T')[0] === new Date().toISOString().split('T')[0] ? "Today's" : date.toISOString().split('T')[0] } sessions
            </Header>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
            {/* No explicit refetch: the query's `variables` are derived from `date`, so Apollo
                reruns it on its own. The calls that used to be here passed the pre-click `date`
                — a stale day — and briefly rendered the wrong sessions. */}
            <Button
                primary
                onClick={() => setDate(new Date(date.getTime() - (24 * 60 * 60 * 1000)))}
            >
                {'<'}
            </Button>
            <Button
                primary
                onClick={() => setDate(new Date())}
            >
                today
            </Button>
            <Button
                primary
                onClick={() => setDate(new Date(date.getTime() + (24 * 60 * 60 * 1000)))}
            >
                {'>'}
            </Button>
      </div>
      <br />
        <Dropdown
            placeholder='Tags'
            fluid
            multiple
            value={tagIds}
            onChange={(_, { value }) => {
                localStorage.setItem('attendanceDashboardTags', JSON.stringify(value));
                setTagIds(value as string[])
            }}
            search
            selection
            options={
                tagsLoading || tagsError ? [] :
                (tagsData && tagsData.tags || [])
                .map((p: any) => ({
                    key: p.id,
                    text: p.name,
                    value: `tagId:${p.id}`,
                }))
            }
        />
        {/* Rendered whenever a filter is set, even if it matches nothing — otherwise picking a
            teacher with no classes that day removed the only way to clear the filter. */}
        {(allSessions.length > 0 || teacherId !== '') && (<Dropdown
            placeholder='All Teachers'
            fluid
            value={teacherId}
            onChange={(_, { value }) => setTeacherId(value === '' ? '' : Number(value))}
            selection
            options={teacherOptions}
        />)}
      <br />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                </div>
                {!isMobile && isAdmin &&
                 <>
                    <div></div><div>
                    <Dropdown
                        placeholder='Reason for cancellation'
                        selection
                        style={{ width: '200px' }}
                        value={reasonForCancellation}
                        onChange={(_, { value }) => {
                            if (CANCELLATION_REASONS.includes(value as string)) {
                                setReasonForCancellation(value as string)
                            }
                        }}
                        options={CANCELLATION_REASONS.map((reason) => ({
                            key: reason,
                            text: reason,
                            value: reason,
                        }))}
                    />
                    <Button
                        primary
                        style={{ marginLeft: '8px' }}
                        disabled={sessions.length === 0}
                        onClick={async () => {
                            // The listed sessions, not the whole day — with a teacher selected,
                            // "all" has to mean the rows on screen.
                            if (window.confirm(`Really cancel the ${sessions.length} class${sessions.length === 1 ? '' : 'es'} listed below? (Use cancel to back out)`)) {
                                for (const session of sessions) {
                                    await deleteSession({ variables: { sessionId: session.id, reason: reasonForCancellation } })
                                }
                                await refetch()
                            }
                        }
                    }
                    >
                        Cancel All Classes
                    </Button>
                     </div>
                </>
                }
            </div>
            {sessions.length > 0 ? (
                <Table celled compact>
                    {!isMobile &&
                <Table.Header>
                    <Table.Row>
                    <Table.HeaderCell>Start Time</Table.HeaderCell>
                    <Table.HeaderCell>Course</Table.HeaderCell>
                    <Table.HeaderCell>Attendance ({overallAttendance}%)</Table.HeaderCell>
                    <Table.HeaderCell>Absents ({overallAbsent})</Table.HeaderCell>
                    <Table.HeaderCell>Roll Complete</Table.HeaderCell>
                    <Table.HeaderCell>Teachers</Table.HeaderCell>
                    <Table.HeaderCell>Cancellation Reason</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>}
                <Table.Body>
                    {sessions.map(renderRow)}
                    </Table.Body>
                </Table>
            ) : (
                <p>{loading ? 'Loading...'
                    : teacherId !== '' && allSessions.length > 0 ? 'No sessions for that teacher on this date.'
                    : 'No sessions scheduled for this date.'}</p>
            )}
        </Container>
      </Segment>
    </ResponsiveContainer>
  )
}

export { Dashboard }
